// utils/axios.js
import axios from "axios";
import { useAuthStore } from "@/composables/store/admin/useAuthStore";
import { useUserAuthStore } from "@/composables/store/user/useAuthStore";
import { useErrorStore } from "@/composables/store/useErrorStore";
import { ERROR_MESSAGES, LOCALSTORAGE } from "./constants";

// -------------------------------
// Instance
// -------------------------------
export const apiInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// -------------------------------
// Request Interceptor (merged)
// -------------------------------
apiInstance.interceptors.request.use(
  (config) => {
    // If sending FormData, let browser set multipart boundaries
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    // Prefer admin token; fall back to user token
    const adminToken = localStorage.getItem(LOCALSTORAGE.ACCESS_TOKEN);
    const userToken = localStorage.getItem(LOCALSTORAGE.USER_ACCESS_TOKEN);

    if (adminToken) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    } else if (userToken) {
      config.headers.Authorization = `Bearer ${userToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// -------------------------------
// Response Interceptor (success)
// -------------------------------
apiInstance.interceptors.response.use(
  (response) => {
    // Special handling for login responses
    if (response.config.url?.includes("/auth/login")) {
      if (response.data.status === "WRONG_CREDENTIALS_ERROR") {
        useErrorStore().setErrorMessage(
          ERROR_MESSAGES[response.data.status] || "Invalid credentials"
        );
        throw response.data;
      }
      if (!response.data.access_token) {
        useErrorStore().setErrorMessage(
          "Login failed - no access token received"
        );
        throw new Error("No access token in login response");
      }
    }

    // Handle standard success statuses
    if (response.status === 200 || response.status === 201) {
      if (response.data?.status === "error") {
        useErrorStore().setErrorMessage(
          response.data.message || "An error occurred"
        );
        throw response.data;
      }
      return response;
    }

    // Non-200 with structured payloads
    if (response.data?.formFields) {
      const errorMessages = response.data.formFields
        .map((f) => f.error)
        .join(", ");
      useErrorStore().setErrorMessage(errorMessages);
      throw response.data;
    } else if (ERROR_MESSAGES[response.data?.status]) {
      useErrorStore().setErrorMessage(ERROR_MESSAGES[response.data.status]);
      throw response.data;
    } else if (response?.statusText !== "OK") {
      useErrorStore().setErrorMessage(
        response?.data?.message || response?.data?.status || "Request failed"
      );
      throw response.data;
    }

    return response;
  },

  // -------------------------------
  // Response Interceptor (error)
  // -------------------------------
  (error) => {
    const errorStore = useErrorStore();

    // --- BEGIN SILENT GUARD ---
    const cfg = error.config || {};
    const url = cfg.url || "";
    const status = error.response?.status;
    const msg =
      error.response?.data?.message ||
      error.response?.data?.status ||
      error.message ||
      "";

    // Per-request opt-out (header or meta)
    const perRequestSilent =
      cfg.headers?.["X-Silent-Error"] === "1" || cfg.meta?.silent === true;

    // Silence the specific "no active token" noise
    const isComplaintInfo404 =
      status === 404 && /\/complaint-url-info$/.test(url);
    const isNoTokenMessage = /No active complaint token/i.test(msg);

    if (perRequestSilent || isComplaintInfo404 || isNoTokenMessage) {
      // Do NOT toast for this error; just bubble it back to caller
      return Promise.reject(error);
    }
    // --- END SILENT GUARD ---

    // 401 handling (skip if failing logout)
    const isLogoutRequest = url.includes("/auth/logout");
    if (status === 401 && !isLogoutRequest) {
      const adminToken = localStorage.getItem(LOCALSTORAGE.ACCESS_TOKEN);
      const userToken = localStorage.getItem(LOCALSTORAGE.USER_ACCESS_TOKEN);

      try {
        if (adminToken) {
          useAuthStore().clearAuthData();
          window.location.href = "/admin/login";
        } else if (userToken) {
          useUserAuthStore().clearAuthData();
          window.location.href = "/login";
        } else {
          window.location.href = "/login";
        }
      } catch {
        window.location.href = "/login";
      }

      return Promise.reject(error);
    }

    // Remaining error branches -> show a toast
    if (error.code === "ERR_BAD_REQUEST") {
      errorStore.setErrorMessage(
        error.response?.data?.message || error.message
      );
    } else if (error.code === "ERR_NETWORK") {
      errorStore.setErrorMessage(ERROR_MESSAGES.SERVER_ERROR);
    } else if (error.response?.formFields) {
      const errorMessages = error.response.formFields
        .map((f) => f.error)
        .join(", ");
      errorStore.setErrorMessage(errorMessages);
    } else if (error?.response?.data?.error) {
      errorStore.setErrorMessage(error.response.data.error);
    } else if (error.response?.message) {
      errorStore.setErrorMessage(error.response.message);
    } else {
      const fallbackError =
        error.message ||
        error.response?.data?.error ||
        "An unknown error occurred";
      errorStore.setErrorMessage(fallbackError);
    }

    return Promise.reject(error);
  }
);

// -------------------------------
// GraphQL helper (unchanged)
// -------------------------------
export async function sendGraphQLRequest(query, variables = {}) {
  try {
    const response = await apiInstance.post("/graphql", { query, variables });
    if (response.data.errors) {
      throw new Error(response.data.errors[0].message);
    }
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Default export for convenience
export default apiInstance;
